import AppPagination from "@app/common/components/AppPagination";
import { warningNotification } from "@app/common/components/notifications";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import { Template } from "@app/common/components/Template";
import { TextField } from "@app/common/components/TextField";
import { dateValidation, formattedDataSelect } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import routes from "@app/config/routes";

import { useGetCompanyClientList, useGetContractList } from "@app/services/common/hooks";

import {
  useDownloadSupportConsultExcel,
  useGetSupportConsultList
} from "@app/services/SupportsConsult/hooks";
import { SupportConsultFilter } from "@app/services/SupportsConsult/types";
import { downloadExcel } from "@app/utils/downloadExcel";
import { ActionIcon, Badge, Grid, Select, Table, Text, TextInput } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SupportsConsult = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SupportConsultFilter>({
    contractId: null,
    corporateClient: null,
    startDate: "",
    endDate: "",
    search: "",
    page: 1
  });

  const {
    data: supportList = {
      results: [],
      rowTotal: 0
    },
    refetch: getSupportsConsult
  } = useGetSupportConsultList(
    {
      ...filters,
      perpage: 10
    },
    dateValidation(filters.endDate, filters.startDate)
  );

  const {
    data: companyList = {
      results: [],
      rowTotal: 0
    }
  } = useGetCompanyClientList({
    status: "enabled",
    eaccount: GCidAccount()
  });

  const {
    data: contractList = {
      results: [],
      rowTotal: 0
    }
  } = useGetContractList(
    {
      corporateClient: Number(filters.corporateClient),
      slim: 1
    },
    filters.corporateClient !== null
  );

  const { refetch: getSupportConsultExcel } = useDownloadSupportConsultExcel({
    ...filters,
    page: undefined,
    perpage: undefined
  });

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    getSupportsConsult();
  };

  const handleDownloadExcel = async () => {
    const response = await getSupportConsultExcel();

    const { success, results } = response.data || {};

    if (success && results?.base64) {
      downloadExcel(results.base64, "consulta_soportes.xlsx");
    } else {
      warningNotification("No se pudo descargar el archivo");
    }
  };

  const renderFiltersHeader = () => {
    return (
      <Grid align="flex-end" mb="lg" w="100%" data-testid="supportsconsults-filters">
        <Grid.Col span={3}>
          <TextInput
            data-testid="supportsconsults-start-date-input"
            label="Fecha desde"
            type="date"
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                startDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            data-testid="supportsconsults-end-date-input"
            label="Fecha hasta"
            min={filters.startDate}
            type="date"
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                endDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            data-testid="supportsconsults-company-select"
            label="Empresa / Cliente"
            data={formattedDataSelect(companyList.results, "name", "id")}
            searchable
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                corporateClient: value ?? "",
                contractId: ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            data-testid="supportsconsults-contract-select"
            label="Contrato"
            searchable
            data={formattedDataSelect(contractList.results, "contractName", "id")}
            placeholder="Seleccionar..."
            value={filters.contractId}
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                contractId: value ?? ""
              }));
            }}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextField
            data-testid="supportsconsults-search-input"
            placeholder="Escribe aquí para buscar..."
            variant="filled"
            className="flex-grow-1 me-4"
            name="search"
            onChange={({ target }) =>
              setFilters((state) => ({
                ...state,
                search: target.value
              }))
            }
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <FilterSearchButton data-testid="supportsconsults-search-button" />
        </Grid.Col>

        <Grid.Col span={4} className="flex justify-end">
          <img
            src="/excel.svg"
            alt="download excel"
            className="pointer"
            data-testid="supportsconsults-download-excel"
            onClick={handleDownloadExcel}
          />
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: (typeof supportList.results)[number], index: number) => {
    const date = (option?.createdAt ?? "").split("T")[0].replaceAll("/", "-");
    return (
      <Table.Tr key={option?.disId} data-testid={`supportsconsults-row-${index}`}>
        <Table.Td>{date}</Table.Td>

        <Table.Td>{option?.patient}</Table.Td>

        <Table.Td ta={"start"}>{option?.patientDocument}</Table.Td>
        <Table.Td ta={"start"}>{option?.corporateClient}</Table.Td>
        <Table.Td ta={"start"}>{option?.contract}</Table.Td>
        <Table.Td ta={"center"}>
          <Badge variant="light" color={"brand-color"}>
            {option?.service}
          </Badge>
        </Table.Td>
        <Table.Td ta={"center"}>{option?.supports}</Table.Td>
        <Table.Td ta={"center"}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            <Badge variant="light" color={option.status.fontColor}>
              {option.status.description}
            </Badge>
          </Text>
        </Table.Td>
        <Table.Td ta="end">
          <ActionIcon variant="subtle" data-testid={`supportsconsults-detail-button-${index}`}>
            <IconChevronRight
              onClick={() =>
                navigate(routes.getSupportsConsultDetail, { state: { id: option.disId } })
              }
            />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="supportsconsults-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"center"}>Fecha</Table.Th>
            <Table.Th>Paciente</Table.Th>
            <Table.Th ta={"start"}>Identificación</Table.Th>
            <Table.Th ta={"start"}>Empresa</Table.Th>
            <Table.Th ta={"start"}>Contrato</Table.Th>
            <Table.Th ta={"start"}>Servicio</Table.Th>
            <Table.Th ta={"center"}>Documentos</Table.Th>
            <Table.Th ta={"center"}>Estado</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {supportList?.results?.length ? (
            supportList?.results?.map((option, index) => renderRow(option, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12} data-testid="supportsconsults-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Consulta de soportes" data-testid="supportsconsults-template">
      <form onSubmit={handleSubmitFilters} data-testid="supportsconsults-filters-form">
        {renderFiltersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        data-testid="supportsconsults-pagination"
        total={supportList?.rowTotal || 0}
        value={filters.page || 1}
        withDetails
        onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
      />
    </Template>
  );
};

export default SupportsConsult;
